import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Select from "react-select";

const FieldLabel = ({ children, required }) => (
  <div
    style={{
      color: "#6b7280",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      marginBottom: 8,
    }}
  >
    {children}
    {required && <span style={{ color: "#ef4444", marginLeft: 4 }}>*</span>}
  </div>
);

const SectionDivider = ({ label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 0 16px" }}>
    <div style={{ flex: 1, height: 1, background: "#f3f4f6" }} />
    <span style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em", textTransform: "uppercase" }}>
      {label}
    </span>
    <div style={{ flex: 1, height: 1, background: "#f3f4f6" }} />
  </div>
);

const inputStyle = {
  width: "100%",
  backgroundColor: "#fff",
  border: "1.5px solid #e5e7eb",
  borderRadius: 10,
  padding: "10px 14px",
  fontSize: 14,
  color: "#111827",
  outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
};

const focusHandlers = {
  onFocus: (e) => {
    e.target.style.borderColor = "#ff5100";
    e.target.style.boxShadow = "0 0 0 3px rgba(255,81,0,0.1)";
  },
  onBlur: (e) => {
    e.target.style.borderColor = "#e5e7eb";
    e.target.style.boxShadow = "none";
  },
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? "#ff5100" : "#e5e7eb",
    borderWidth: "1.5px",
    borderRadius: 10,
    minHeight: 42,
    boxShadow: state.isFocused ? "0 0 0 3px rgba(255,81,0,0.1)" : "none",
    backgroundColor: "#fff",
    ":hover": { borderColor: state.isFocused ? "#ff5100" : "#d1d5db" },
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 10px", fontSize: 14 }),
  placeholder: (base) => ({ ...base, color: "#9ca3af", fontSize: 14 }),
  singleValue: (base) => ({ ...base, color: "#111827", fontSize: 14 }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "rgba(255,81,0,0.08)",
    borderRadius: 6,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#ff5100",
    fontSize: 12,
    fontWeight: 600,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#ff5100",
    ":hover": { backgroundColor: "rgba(255,81,0,0.15)", color: "#c13d00" },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    borderRadius: 10,
    border: "1.5px solid #e5e7eb",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    overflow: "hidden",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "rgba(255,81,0,0.08)"
      : state.isFocused
      ? "#f9fafb"
      : "#fff",
    color: state.isSelected ? "#ff5100" : "#111827",
    fontSize: 14,
    fontWeight: state.isSelected ? 600 : 400,
    cursor: "pointer",
  }),
  groupHeading: (base) => ({
    ...base,
    fontSize: 10,
    fontWeight: 700,
    color: "#9ca3af",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    padding: "8px 12px 4px",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, color: "#9ca3af", padding: "0 8px" }),
};

const EditAccount = ({
  domain,
  show,
  userId,
  clientId,
  handleClose,
  handleFormSubmit,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [organizations, setOrganizations] = useState([]);
  const [orgOption, setOrgOption] = useState(null);
  const [pendingOrgId, setPendingOrgId] = useState(null);

  const userTypeOptions = useMemo(
    () => [
      { value: "backoffice", label: "Back Office" },
      { value: "crmmanager", label: "CRM Manager" },
      { value: "customadmin", label: "Custom Admin" },
      { value: "superadmin", label: "Superadmin (Caution)" },
    ],
    []
  );
  const [userTypeOption, setUserTypeOption] = useState(null);

  const groupedPermissions = useMemo(
    () => [
      {
        label: "CRM",
        options: [{ value: "escalateReports", label: "Escalate Reports" }],
        adminOnly: false,
      },
      {
        label: "BLEEP Map",
        options: [{ value: "bleepMap", label: "View BLEEP Map" }],
        adminOnly: false,
      },
      {
        label: "Product SKUs",
        options: [
          { value: "productSkus", label: "View Product SKUs" },
          { value: "createProductSkus", label: "Create Product SKUs" },
          { value: "deleteProductSkus", label: "Delete Product SKUs" },
          { value: "editProductSkus", label: "Edit Product SKUs" },
        ],
        adminOnly: false,
      },
      {
        label: "BleST Orders",
        options: [
          { value: "blestOrders", label: "View BleST Orders" },
          { value: "placeOrders", label: "Place BleST Orders" },
        ],
        adminOnly: true,
      },
      {
        label: "My BleST",
        options: [{ value: "myBlest", label: "View My BleST" }],
        adminOnly: false,
      },
      {
        label: "BleST Map",
        options: [{ value: "blestMap", label: "View BleST Map" }],
        adminOnly: false,
      },
      {
        label: "Ads",
        options: [
          { value: "ads", label: "View Ads" },
          { value: "createAds", label: "Create Ads" },
          { value: "deleteAds", label: "Delete Ads" },
          { value: "editAds", label: "Edit Ads" },
        ],
        adminOnly: false,
      },
      {
        label: "Clients",
        options: [
          { value: "clients", label: "View Clients" },
          { value: "createClients", label: "Create Clients" },
          { value: "deleteClients", label: "Delete Clients" },
          { value: "editClients", label: "Edit Clients" },
        ],
        adminOnly: true,
      },
      {
        label: "Accounts",
        options: [
          { value: "accounts", label: "View Accounts" },
          { value: "createAccounts", label: "Create Accounts" },
          { value: "deleteAccounts", label: "Delete Accounts" },
          { value: "editAccounts", label: "Edit Accounts" },
        ],
        adminOnly: false,
      },
      {
        label: "Articles",
        options: [{ value: "articles", label: "Manage Articles" }],
        adminOnly: false,
        internalOnly: true,
      },
    ],
    []
  );

  const [selectedPermissionOptions, setSelectedPermissionOptions] = useState([]);

  const selectedOrgName = orgOption?.label || "";
  const isSpecialOrg =
    selectedOrgName === "Greenbridge" || selectedOrgName === "ACN";
  const isCustomAdmin = isSpecialOrg && userTypeOption?.value === "customadmin";

  const isBackoffice = userTypeOption?.value === "backoffice";

  const filteredPermissionOptions = useMemo(() => {
    return groupedPermissions
      .map((group) => {
        if (group.adminOnly && !isCustomAdmin) return null;
        if (group.internalOnly && !isSpecialOrg) return null;
        return { label: group.label, options: group.options };
      })
      .filter(Boolean);
  }, [groupedPermissions, isCustomAdmin, isSpecialOrg]);

  const resetState = () => {
    setUsername("");
    setEmail("");
    setPassword("");
    setOrgOption(null);
    setPendingOrgId(null);
    setUserTypeOption(null);
    setSelectedPermissionOptions([]);
  };

  useEffect(() => {
    const loadOrgs = async () => {
      try {
        const res = await fetch("/api/organizations");
        if (!res.ok) throw new Error("Failed to fetch organizations");
        const data = await res.json();
        const opts = (data || []).map((o) => ({
          value: o._id,
          label: o.organization,
          raw: o,
        }));
        setOrganizations(opts);
      } catch (e) {
        console.error(e);
      }
    };
    loadOrgs();
  }, []);

  useEffect(() => {
    if (!clientId || organizations.length === 0) return;
    const match = organizations.find((o) => o.value === clientId);
    if (match) {
      setOrgOption(match);
      setPendingOrgId(clientId);
    }
  }, [clientId, organizations]);

  useEffect(() => {
    if (!show || !userId) return;
    setLoading(true);
    const run = async () => {
      try {
        const res = await fetch(`/api/users/${userId}`, {
          method: "GET",
          redirect: "follow",
        });
        const raw = await res.text();
        const data = raw ? JSON.parse(raw) : {};
        setUsername(data.username || "");
        setEmail(data.email || "");
        setPassword("");

        const userOrgId =
          data.organizationId ||
          data.organization?._id ||
          data.organization ||
          null;
        setPendingOrgId(clientId || userOrgId || null);

        let permsObj = {};
        if (typeof data.permissions === "string") {
          try {
            permsObj = JSON.parse(data.permissions || "{}");
          } catch {
            permsObj = {};
          }
        } else if (
          typeof data.permissions === "object" &&
          data.permissions !== null
        ) {
          permsObj = data.permissions;
        }

        const flatOptions = groupedPermissions.flatMap((g) => g.options);
        const selected = flatOptions.filter((opt) => permsObj[opt.value]);
        setSelectedPermissionOptions(selected);

        const typeVal = (data.type || "").toLowerCase();
        const foundType =
          userTypeOptions.find((o) => o.value === typeVal) || null;
        setUserTypeOption(foundType);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [show, userId, groupedPermissions, userTypeOptions, clientId]);

  useEffect(() => {
    if (!pendingOrgId || organizations.length === 0) return;
    const opt = organizations.find((o) => o.value === pendingOrgId) || null;
    setOrgOption(opt);
  }, [pendingOrgId, organizations]);

  useEffect(() => {
    if (!show) resetState();
  }, [show]);

  useEffect(() => {
    if (isSpecialOrg && !isCustomAdmin && !isBackoffice && selectedPermissionOptions.length) {
      setSelectedPermissionOptions([]);
    }
  }, [isSpecialOrg, isCustomAdmin, isBackoffice, selectedPermissionOptions.length]);

  const handleOrgChange = (opt) => {
    if (clientId) return;
    setOrgOption(opt);
    setSelectedPermissionOptions([]);
  };

  const handleTypeChange = (opt) => {
    if (userTypeOption?.value === "customadmin" && opt?.value !== "customadmin") {
      setSelectedPermissionOptions([]);
    }
    setUserTypeOption(opt);
  };

  const handlePermissionsChange = (opts) => {
    setSelectedPermissionOptions(opts || []);
  };

  const updateUser = async () => {
    if (!username || !email || !orgOption) {
      alert("Please fill in required fields.");
      return;
    }

    let type = "";
    let permissions = {};

    if (isSpecialOrg) {
      if (!userTypeOption) {
        alert("Please select a user type.");
        return;
      }
      type = userTypeOption.value;
      if (type === "customadmin" || type === "backoffice") {
        permissions = selectedPermissionOptions.reduce((acc, opt) => {
          acc[opt.value] = true;
          return acc;
        }, {});
      } else {
        permissions = {};
      }
    } else {
      type = "brandadmin";
      permissions = selectedPermissionOptions.reduce((acc, opt) => {
        acc[opt.value] = true;
        return acc;
      }, {});
    }

    const payload = {
      username,
      email: email.trim().toLowerCase(),
      ...(password ? { password } : {}),
      type: String(type).toLowerCase(),
      organization: orgOption.value,
      permissions,
    };

    setSaving(true);
    try {
      const res = await fetch(`/api/users/${userId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const text = await res.text();
      let body = {};
      try {
        body = text ? JSON.parse(text) : {};
      } catch {
        body = { message: text || "Unknown response" };
      }
      if (!res.ok) {
        alert(body?.message || "Error updating user");
        return;
      }
      handleClose();
      handleFormSubmit();
    } catch (e) {
      console.error(e);
      alert("Error updating user");
    } finally {
      setSaving(false);
    }
  };

  const hideOrganizationSelect = !!clientId;
  const shouldShowPermissions = !!orgOption && (!isSpecialOrg || isCustomAdmin || isBackoffice);
  const disabled = saving || loading;

  return (
    <>
      <style>{`
        .edit-account-modal .modal-content {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.12);
        }
        .edit-account-modal .modal-header {
          background: #fff;
          border-bottom: 1px solid #f3f4f6;
          border-radius: 18px 18px 0 0;
          padding: 20px 24px 16px;
        }
        .edit-account-modal .modal-body {
          background: #fff;
          padding: 20px 24px;
        }
        .edit-account-modal .modal-footer {
          background: #f9fafb;
          border-top: 1px solid #f3f4f6;
          border-radius: 0 0 18px 18px;
          padding: 16px 24px;
        }
      `}</style>

      <Modal
        show={show}
        onHide={handleClose}
        centered
        dialogClassName="edit-account-modal"
        style={{ backdropFilter: "blur(3px)" }}
      >
        <Modal.Header closeButton>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34, height: 34, borderRadius: 9,
                background: "rgba(255,81,0,0.08)",
                border: "1.5px solid rgba(255,81,0,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <i className="fas fa-user-edit" style={{ color: "#ff5100", fontSize: 13 }} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#111827" }}>Edit Account</div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>Update user account details</div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {loading ? (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#9ca3af" }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: 22, marginBottom: 10, display: "block" }} />
              <div style={{ fontSize: 13, fontWeight: 600 }}>Loading account…</div>
            </div>
          ) : (
            <>
              {/* Display Name */}
              <div>
                <FieldLabel required>Display Name</FieldLabel>
                <input
                  type="text"
                  placeholder="John Doe"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={disabled}
                  style={{ ...inputStyle, opacity: disabled ? 0.6 : 1 }}
                  {...focusHandlers}
                />
              </div>

              {/* Email */}
              <div style={{ marginTop: 16 }}>
                <FieldLabel required>Email Address</FieldLabel>
                <input
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={disabled}
                  style={{ ...inputStyle, opacity: disabled ? 0.6 : 1 }}
                  {...focusHandlers}
                />
              </div>

              {/* Password */}
              <div style={{ marginTop: 16 }}>
                <FieldLabel>
                  New Password
                  <span style={{ fontWeight: 400, textTransform: "none", marginLeft: 6, fontSize: 11, color: "#9ca3af" }}>
                    leave blank to keep current
                  </span>
                </FieldLabel>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={disabled}
                    style={{ ...inputStyle, paddingRight: 44, opacity: disabled ? 0.6 : 1 }}
                    {...focusHandlers}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    style={{
                      position: "absolute", right: 12, top: "50%",
                      transform: "translateY(-50%)", background: "none",
                      border: "none", cursor: "pointer", padding: 4,
                      color: "#9ca3af", lineHeight: 1,
                    }}
                  >
                    <i className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`} style={{ fontSize: 14 }} />
                  </button>
                </div>
              </div>

              {/* Client select */}
              {!hideOrganizationSelect && (
                <div style={{ marginTop: 16 }}>
                  <FieldLabel required>Client</FieldLabel>
                  <Select
                    inputId="organization"
                    placeholder="Select client…"
                    options={organizations}
                    value={orgOption}
                    onChange={handleOrgChange}
                    styles={selectStyles}
                    isDisabled={organizations.length === 0 || disabled}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
              )}

              {/* Internal org indicator */}
              {isSpecialOrg && (
                <div
                  style={{
                    marginTop: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    background: "rgba(255,81,0,0.05)",
                    border: "1.5px solid rgba(255,81,0,0.2)",
                    borderRadius: 10,
                    padding: "10px 14px",
                  }}
                >
                  <div
                    style={{
                      width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                      background: "rgba(255,81,0,0.1)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <i className="fas fa-shield-alt" style={{ fontSize: 12, color: "#ff5100" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#ff5100" }}>Internal</div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>
                      This client is an internal organisation. Select a user type below.
                    </div>
                  </div>
                </div>
              )}

              {/* User type select — special orgs only */}
              {isSpecialOrg && (
                <div style={{ marginTop: 12 }}>
                  <FieldLabel required>User Type</FieldLabel>
                  <Select
                    inputId="type"
                    placeholder="Select user type…"
                    options={userTypeOptions}
                    value={userTypeOption}
                    onChange={handleTypeChange}
                    styles={selectStyles}
                    isDisabled={disabled}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
              )}

              {/* Permissions */}
              {shouldShowPermissions && (
                <>
                  <SectionDivider label="Permissions" />
                  <Select
                    inputId="permissions"
                    isMulti
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    options={filteredPermissionOptions}
                    value={selectedPermissionOptions}
                    onChange={handlePermissionsChange}
                    placeholder="Select permissions…"
                    styles={selectStyles}
                    classNamePrefix="rs-perms"
                    isDisabled={disabled}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            onClick={updateUser}
            disabled={disabled}
            style={{
              width: "100%",
              padding: "11px 0",
              borderRadius: 10,
              border: "none",
              background: disabled ? "#e5e7eb" : "linear-gradient(135deg, #c13d00 0%, #ff5100 100%)",
              color: disabled ? "#9ca3af" : "#fff",
              fontWeight: 700,
              fontSize: 15,
              cursor: disabled ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 9,
              transition: "background 0.15s",
            }}
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: 13 }} />
                Saving…
              </>
            ) : (
              <>
                Save Changes
                <i className="fas fa-arrow-right" style={{ fontSize: 13 }} />
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditAccount;
